import {
  Box,
  Card,
  CardContent,
  Dialog, DialogContent, Divider, Typography, useTheme,
} from '@mui/material';
import { UpdateCompanyProfileInput } from '@optimus/models';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import { FormProvider } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  selectGetCompanyPartnerRelationsState,
} from '../../../../store/slices/dashboardSlice';
import LennoxAccountNumberCardSkeleton from '../../../../common/LennoxAccountNumberCardSkeleton';
import {
  DialogCTAComponent, FormMethods, SnackbarComponent,
  updatedCompanyRequest,
} from './PartnerEnrollmentCardCommon';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import { MemberInformationFormControl } from './MemberInformationFormControl';
import { BusinessInformationFormControl } from './BusinessInformationFormControl';
import { IFormInputs } from '../../../../common/StyledFormInputs';
import { validateField } from '../../../../common/utils/formUtils';
import { TestIds } from '../../../../assets/testIds';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import { updateCompanyProfile } from '../../../../api/dashboardAPI';
import { getMemberInformation } from '../../../../store/thunks/onboardingThunk';

interface CompanyProfileDialogProps {
  updateOpenState?: (event: boolean) => void;
  updatePartnerOpenState?: (value: boolean) => void;
  optionalPostEnrollmentAction?: () => void
}

export default function CompanyProfileDialog({
  updateOpenState,
  updatePartnerOpenState,
  optionalPostEnrollmentAction,
}: CompanyProfileDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { trackAccountSetting, trackRequest } = useCustomEventTracking();
  const getCompanyPartnerRelationsState = useAppSelector(selectGetCompanyPartnerRelationsState);
  const memberInfo = useAppSelector(selectMemberInformation);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = React.useState(true);

  const methods = FormMethods(memberInfo);
  const {
    getValues, handleSubmit, formState: { isValid },
  } = methods;

  const handleCloseEnrollmentDialog = () => {
    // reset all to default states
    setOpen(false);
    updateOpenState?.(false);
    updatePartnerOpenState?.(false);
  };

  const handleSnackbarClose = () => {
    setErrorMessage('');
    setSnackbarOpen(false);
  };

  const checkDataBeforeSend = (data: IFormInputs): void => {
    try {
      // Validate MemberInfo fields
      validateField(data.memberInfo.firstName, `${t('onboarding.questionPlaceholder.First Name')}`, t('error.required'));
      validateField(data.memberInfo.lastName, `${t('onboarding.questionPlaceholder.Last Name')}`, t('error.required'));

      // Validate BusinessInfo fields
      validateField(data.businessInfo.name, `${t('businessInformationForm.name')}`, t('error.required'));
      validateField(data.businessInfo.email, `${t('businessInformationForm.email')}`, t('error.required'));
      validateField(data.businessInfo.phone, `${t('businessInformationForm.phone')}`, t('error.required'));
      // Website is not required but if provided must be valid
      if (data.businessInfo.website) {
        validateField(data.businessInfo.website, `${t('businessInformationForm.website')}`, t('error.invalid'));
      }

      // Validate business address
      validateField(data.businessInfo.autoAddress?.fullAddress, `${t('businessInformationForm.address.heading')}`, t('error.required'));
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(`${t('error.generalMessage')}`);
      }
    }
  };

  const onSubmit = (data: IFormInputs) => {
    if (data) {
      const generalErrorMessage = t('error.generalMessage3');
      try {
        checkDataBeforeSend(data);
        setLoading(true);

        const updatedMemberInfoRequest = {
          firstName: getValues('memberInfo.firstName'),
          lastName: getValues('memberInfo.lastName'),
        };

        const companyProfileUpdateRequest: UpdateCompanyProfileInput = {
          memberInfoUpdate: updatedMemberInfoRequest,
          companyInfoUpdate: updatedCompanyRequest(getValues),
        };

        updateCompanyProfile(companyProfileUpdateRequest, memberInfo?.company?.id as string)
          .then(() => {
            setLoading(false);
            updateOpenState?.(false);
            optionalPostEnrollmentAction?.();
            dispatch(getMemberInformation({ trackRequest }));
            trackAccountSetting('Update', 'CompanyProfile');
          })
          .catch((error) => {
            if (error instanceof Error) {
              setErrorMessage(error.message);
            } else {
              setErrorMessage(generalErrorMessage);
            }
            setSnackbarOpen(true);
            setLoading(false);
          });
      } catch (error) {
        setErrorMessage(generalErrorMessage);
        setSnackbarOpen(true);
        setLoading(false);
      }
    }
  };

  const DialogHeader = () => (
    <>
      <Card>
        <CardContent
          sx={{
            padding: `${theme.spacing(2)} ${theme.spacing(0)} `,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: theme.spacing(6),
              height: theme.spacing(6),
              padding: theme.spacing(1.5),
              borderRadius: theme.spacing(1.25),
              border: `1px solid ${theme.palette.grey[200]}`,
              marginBottom: theme.spacing(2),
            }}
          >
            <PermIdentityIcon />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: theme.spacing(0.5),
              alignSelf: 'stretch',
            }}>
            <Typography variant="h4" component="div" align="center">
              {t('companyProfile.dialog.heading1')}
            </Typography>
            <Typography variant="body8" component="div" align="center" color={theme.palette.grey[700]}>
              {t('companyProfile.dialog.heading2')}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseEnrollmentDialog}
            sx={{
              position: 'absolute',
              right: theme.spacing(2),
              top: theme.spacing(3.5),
              width: theme.spacing(6),
              height: theme.spacing(6),
            }}
          >
            <CloseIcon />
          </IconButton>
        </CardContent>
      </Card>
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        keepMounted
        onClose={handleCloseEnrollmentDialog}
        aria-describedby="company-profile-form-dialog"
        sx={{
          '& .MuiDialog-paper': {
            width: '100%',
            borderRadius: theme.spacing(2.25),
            [theme.breakpoints.down('lg')]: {
              p: theme.spacing(0),
            },
          },
        }}
      >
        <DialogContent
          sx={{ paddingRight: theme.spacing(6.25) }}
        >
          <DialogHeader />
          <Divider sx={{
            mb: theme.spacing(3),
          }} />
          {getCompanyPartnerRelationsState === 'pending' ?
            <LennoxAccountNumberCardSkeleton /> :
            <>
              <FormProvider {...methods} >
                <form onSubmit={handleSubmit(onSubmit)}>
                  <>
                    <MemberInformationFormControl
                      hasEmptyFields
                      isProfileForm
                    />
                    <BusinessInformationFormControl
                      hasEmptyFields
                      isHdSupplyForm={false}
                      isProfileForm
                    />
                  </>
                  <DialogCTAComponent
                    loading={loading}
                    submitBtnTestId={TestIds.partnersPage.companyProfile.signUpForm.submitBtn}
                    closeBtnTestId={TestIds.partnersPage.companyProfile.signUpForm.closeBtn}
                    isProfileForm
                    extraDisableCondition={!isValid}
                  />
                </form>
              </FormProvider>
              {/* Snackbar for Error Display */}
              <SnackbarComponent
                snackbarOpen={snackbarOpen}
                errorMessage={errorMessage}
                handleSnackbarClose={handleSnackbarClose}
              />
            </>
          }
        </DialogContent>
      </Dialog>
    </>
  );
}
